import { useSearchParams } from 'react-router-dom';

import { Product } from '@interfaces/products.interface';
import { Button, Divider, Table, Text } from '@mantine/core';
import { openModal } from '@mantine/modals';
import { addSpacesAfterCommas } from '@utils/strings';

import ProductModal, { PRODUCT_MODAL_ID } from './productModal/ProductModal';
import { useTranslation } from 'react-i18next';

interface ProductTableRowProps {
  product: Product;
  showConfColumn: boolean;
  isMobile: boolean;
  marketCategory: string;
}

export default function ProductTableRow({
  product,
  showConfColumn = false,
  isMobile,
  marketCategory,
}: ProductTableRowProps) {
  // ==========================================================================
  // General
  // ==========================================================================
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();

  // ==========================================================================
  // Handlers
  // ==========================================================================
  const openProductModal = () => {
    openModal({
      modalId: PRODUCT_MODAL_ID,
      children: (
        <ProductModal
          key={product.code}
          product={product}
          category={searchParams.get('market')!}
        />
      ),
      size: '60rem',
    });
  };

  // ==========================================================================
  // Render
  // ==========================================================================
  return (
    <Table.Tr
      key={product.code}
      className={`marketcategory-${searchParams.get('market')!.toLowerCase() ?? ''}`}
      onClick={isMobile ? () => openProductModal() : undefined}
    >
      <Table.Td>
        <Text c="brand" size="sm" fw="600">
          {product.name}
        </Text>
        <Text>
          {product.supplier && (
            <Text span size="xs" fw="500">
              {product.supplier} -{' '}
            </Text>
          )}
          <Text span fs="italic">
            {product.code}
          </Text>
        </Text>
        {marketCategory === 'Cosmetico' && (
          <Text size="xs" fw="600" mb="xs">
            {t('products.ingredients')}:{' '}
            <Text span fs="italic">
              {product.INCIName}
            </Text>
          </Text>
        )}
        <Text size="sm" fw="500">
          {product.description ?? ''}
        </Text>
      </Table.Td>
      <Table.Td style={{ maxWidth: '8rem' }}>
        {marketCategory === 'Cosmetico' ? (
          <Text size="xs" fs="italic">
            {product.classifications ?? '-'}
          </Text>
        ) : !product.applications && !product.reactivity ? (
          '-'
        ) : (
          <>
            <Text size="xs" mb="xs">
              {product.applications
                ? addSpacesAfterCommas(product.applications)
                : '-'}
            </Text>
            <Divider />
            <Text size="xs" mt="xs">
              {product.reactivity
                ? addSpacesAfterCommas(product.reactivity)
                : '-'}
            </Text>
          </>
        )}
      </Table.Td>
      {showConfColumn && (
        <Table.Td>
          <Text size="xs">{product.packaging ?? '-'}</Text>
        </Table.Td>
      )}
      {!isMobile && (
        <Table.Td>
          <Button size="xs" onClick={() => openProductModal()}>
            {t('products.details')}
          </Button>
        </Table.Td>
      )}
    </Table.Tr>
  );
}
